import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Chip,
  Stack,
  IconButton,
  Alert,
  Paper,
  Divider,
  alpha
} from '@mui/material';
import {
  TimePicker,
  DatePicker
} from '@mui/x-date-pickers';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
  Repeat as RepeatIcon
} from '@mui/icons-material';
import { format, addWeeks, startOfWeek, addDays, isBefore, isAfter } from 'date-fns';
import { es } from 'date-fns/locale';

const DIAS_SEMANA = [
  { valor: 1, nombre: 'Lunes', abrev: 'L' },
  { valor: 2, nombre: 'Martes', abrev: 'M' },
  { valor: 3, nombre: 'Miércoles', abrev: 'X' },
  { valor: 4, nombre: 'Jueves', abrev: 'J' },
  { valor: 5, nombre: 'Viernes', abrev: 'V' },
  { valor: 6, nombre: 'Sábado', abrev: 'S' },
  { valor: 0, nombre: 'Domingo', abrev: 'D' }
];

const RecurrenceScheduler = ({ 
  patrones = [], 
  onPatronesChange, 
  fechaInicio,
  onFechaInicioChange,
  disabled = false,
  pacientesSeleccionados = [] // Para citas grupales
}) => {
  const [nuevoPatron, setNuevoPatron] = useState({
    diaSemana: '',
    horaInicio: null,
    horaFin: null,
    cantidadSesiones: 8
  });

  // Agregar nuevo patrón
  const handleAgregarPatron = () => {
    if (!nuevoPatron.diaSemana || !nuevoPatron.horaInicio || !nuevoPatron.horaFin || !nuevoPatron.cantidadSesiones) {
      return;
    }

    // Verificar que no exista ya ese día
    const existeElDia = patrones.some(p => p.diaSemana === nuevoPatron.diaSemana);
    if (existeElDia) {
      alert('Ya existe un patrón para ese día de la semana');
      return;
    }

    const patronFormateado = {
      id: Date.now(), // ID único temporal
      diaSemana: nuevoPatron.diaSemana,
      diaNombre: DIAS_SEMANA.find(d => d.valor === nuevoPatron.diaSemana)?.nombre,
      horaInicio: nuevoPatron.horaInicio,
      horaFin: nuevoPatron.horaFin,
      cantidadSesiones: nuevoPatron.cantidadSesiones
    };

    onPatronesChange([...patrones, patronFormateado]);

    // Limpiar formulario
    setNuevoPatron({
      diaSemana: '',
      horaInicio: null,
      horaFin: null,
      cantidadSesiones: 8
    });
  };

  // Eliminar patrón
  const handleEliminarPatron = (patronId) => {
    onPatronesChange(patrones.filter(p => p.id !== patronId));
  };

  // Generar preview de fechas
  const generarPreviewFechas = () => {
    if (!fechaInicio || patrones.length === 0) return [];

    const todasLasFechas = [];

    patrones.forEach(patron => {
      const fechasDelPatron = [];
      let fechaActual = startOfWeek(fechaInicio, { weekStartsOn: 1 }); // Lunes como inicio de semana
      
      // Ajustar al día correcto de la semana
      fechaActual = addDays(fechaActual, patron.diaSemana === 0 ? 6 : patron.diaSemana - 1);
      
      // Si la fecha calculada es anterior a la fecha de inicio, mover a la siguiente semana
      if (isBefore(fechaActual, fechaInicio)) {
        fechaActual = addWeeks(fechaActual, 1);
      }

      // Generar todas las sesiones para este patrón
      for (let i = 0; i < patron.cantidadSesiones; i++) {
        const fechaCompleta = new Date(fechaActual);
        fechaCompleta.setHours(patron.horaInicio.getHours(), patron.horaInicio.getMinutes(), 0, 0);

        fechasDelPatron.push({
          fecha: fechaCompleta,
          patron: patron,
          sesion: i + 1
        });

        fechaActual = addWeeks(fechaActual, 1);
      }

      todasLasFechas.push(...fechasDelPatron);
    });

    // Ordenar por fecha
    return todasLasFechas.sort((a, b) => a.fecha - b.fecha);
  };

  const previewFechas = generarPreviewFechas();
  const totalSesiones = patrones.reduce((total, patron) => total + patron.cantidadSesiones, 0);

  return (
    <Card elevation={2}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2} mb={3}>
          <RepeatIcon color="warning" />
          <Typography variant="h6" color="warning.main">
            Programación Recurrente
          </Typography>
        </Stack>

        <Grid container spacing={3}>
          {/* Fecha de inicio */}
          <Grid item xs={12} md={4}>
            <DatePicker
              label="Fecha de Inicio de la Recurrencia"
              value={fechaInicio}
              onChange={onFechaInicioChange}
              disabled={disabled}
              slotProps={{
                textField: {
                  fullWidth: true,
                  helperText: "Primera semana desde la cual aplicar el patrón"
                }
              }}
            />
          </Grid>

          {/* Formulario para nuevo patrón */}
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: alpha('#ff9800', 0.05), border: '1px solid', borderColor: alpha('#ff9800', 0.2) }}>
              <Typography variant="subtitle2" gutterBottom color="warning.main">
                ➕ Agregar Nuevo Patrón Semanal
              </Typography>
              
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Día de la Semana</InputLabel>
                    <Select
                      value={nuevoPatron.diaSemana}
                      onChange={(e) => setNuevoPatron(prev => ({ ...prev, diaSemana: e.target.value }))}
                      label="Día de la Semana"
                      disabled={disabled}
                    >
                      {DIAS_SEMANA.map(dia => (
                        <MenuItem key={dia.valor} value={dia.valor}>
                          {dia.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={2.5}>
                  <TimePicker
                    label="Hora Inicio"
                    value={nuevoPatron.horaInicio}
                    onChange={(hora) => setNuevoPatron(prev => ({ ...prev, horaInicio: hora }))}
                    disabled={disabled}
                    slotProps={{
                      textField: { size: 'small', fullWidth: true }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={2.5}>
                  <TimePicker
                    label="Hora Fin"
                    value={nuevoPatron.horaFin}
                    onChange={(hora) => setNuevoPatron(prev => ({ ...prev, horaFin: hora }))}
                    disabled={disabled}
                    slotProps={{
                      textField: { size: 'small', fullWidth: true }
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={2}>
                  <TextField
                    label="Sesiones"
                    type="number"
                    size="small"
                    fullWidth
                    value={nuevoPatron.cantidadSesiones}
                    onChange={(e) => setNuevoPatron(prev => ({ ...prev, cantidadSesiones: parseInt(e.target.value) || 1 }))}
                    inputProps={{ min: 1, max: 52 }}
                    disabled={disabled}
                  />
                </Grid>

                <Grid item xs={12} sm={1}>
                  <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAgregarPatron}
                    disabled={disabled || !nuevoPatron.diaSemana || !nuevoPatron.horaInicio || !nuevoPatron.horaFin}
                    fullWidth
                  >
                    +
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Lista de patrones configurados */}
          {patrones.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                📋 Patrones Configurados ({totalSesiones} sesiones total)
              </Typography>
              
              <Stack spacing={1}>
                {patrones.map(patron => (
                  <Paper key={patron.id} elevation={1} sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Chip 
                          label={patron.diaNombre} 
                          color="warning" 
                          variant="outlined"
                          size="small"
                        />
                        <Typography variant="body2">
                          {format(patron.horaInicio, 'HH:mm')} - {format(patron.horaFin, 'HH:mm')}
                        </Typography>
                        <Chip 
                          label={`${patron.cantidadSesiones} sesiones`} 
                          color="info" 
                          size="small"
                        />
                        {pacientesSeleccionados.length > 0 && (
                          <Chip 
                            label={`${pacientesSeleccionados.length} pacientes`} 
                            color="secondary" 
                            size="small"
                          />
                        )}
                      </Stack>
                      
                      <IconButton
                        onClick={() => handleEliminarPatron(patron.id)}
                        color="error"
                        size="small"
                        disabled={disabled}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Grid>
          )}

          {/* Preview de fechas generadas */}
          {previewFechas.length > 0 && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" gutterBottom>
                📅 Preview de Citas a Crear ({previewFechas.length} total)
              </Typography>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                Se crearán {previewFechas.length} citas desde {format(previewFechas[0].fecha, 'dd/MM/yyyy')} hasta {format(previewFechas[previewFechas.length - 1].fecha, 'dd/MM/yyyy')}
                {pacientesSeleccionados.length > 0 && (
                  <> • Total de registros: {previewFechas.length * pacientesSeleccionados.length}</>
                )}
              </Alert>

              <Box sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                <Grid container spacing={1}>
                  {previewFechas.slice(0, 20).map((item, index) => ( // Mostrar solo primeras 20
                    <Grid item xs={6} sm={4} md={3} key={index}>
                      <Paper elevation={0} sx={{ p: 1, bgcolor: alpha(item.patron.diaSemana === 1 ? '#2196f3' : '#ff9800', 0.1) }}>
                        <Typography variant="caption" display="block">
                          {format(item.fecha, 'dd/MM/yyyy')} • {item.patron.diaNombre}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {format(item.fecha, 'HH:mm')} • Sesión #{item.sesion}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                  {previewFechas.length > 20 && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" textAlign="center">
                        ... y {previewFechas.length - 20} citas más
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default RecurrenceScheduler;